import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, resolveMedia, isPdfUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Wand2 } from "lucide-react";

function formatDate(iso) { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } }

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/news"); setItems(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function del(id) {
    if (!window.confirm("Diese News wirklich löschen?")) return;
    try { await api.delete(`/news/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function backfillPdfPreviews() {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/backfill-pdf-previews");
      const { data: m } = await api.post("/admin/backfill-media-pdf-previews");
      const heroLine = `${data.processed} Beitragsbild-Vorschau(en)`;
      const mediaLine = `${m.processed} Anhang-Vorschau(en) in ${m.updated_news} News`;
      toast.success(`${heroLine} · ${mediaLine}${(data.failed + m.failed) ? ` · ${data.failed + m.failed} fehlgeschlagen` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Backfill fehlgeschlagen"); }
    finally { setBackfilling(false); }
  }

  // Show the button if EITHER article-hero PDFs OR media-PDFs may be missing previews.
  // (We can't check media[].preview_url from the list endpoint easily — always show
  // the button when at least one article exists; clicking it is idempotent.)
  const needsBackfill = items.some((n) => (n.image_url && isPdfUrl(n.image_url) && !n.image_preview_url)) || items.length > 0;

  return (
    <div data-testid="admin-news-list">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-semibold text-3xl mb-1">News</h1>
          <p className="text-sm text-neutral-500">Meldungen rund um das Quartier verwalten.</p>
        </div>
        <div className="flex items-center gap-3">
          {needsBackfill && (
            <button
              type="button"
              onClick={backfillPdfPreviews}
              disabled={backfilling}
              data-testid="admin-news-backfill"
              title="Generiert für alle PDF-Beitragsbilder ein Vorschaubild (erste Seite)"
              className="inline-flex items-center gap-2 border border-brand-line bg-white px-4 py-2.5 text-sm hover:bg-neutral-50 disabled:opacity-50"
            >
              <Wand2 className="h-4 w-4" /> {backfilling ? "Erzeuge…" : "PDF-Vorschauen erzeugen"}
            </button>
          )}
          <Link to="/admin/news/new" data-testid="admin-news-new" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]">
            <Plus className="h-4 w-4" /> Neue News
          </Link>
        </div>
      </div>

      <div className="bg-white border border-brand-line overflow-hidden">
        {loading ? (
          <p className="p-8 text-sm text-neutral-500">Lade…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-sm text-neutral-500">Noch keine News angelegt.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-5 py-3 font-normal">Titel</th>
                <th className="text-left px-5 py-3 font-normal">Datum</th>
                <th className="text-left px-5 py-3 font-normal">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-brand-line" data-testid={`news-row-${n.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {n.image_url && (
                        isPdfUrl(n.image_url) ? (
                          n.image_preview_url ? (
                            <div className="relative h-10 w-14 flex-shrink-0">
                              <img src={resolveMedia(n.image_preview_url)} alt="" className="h-10 w-14 object-cover" />
                              <FileText className="absolute top-0.5 left-0.5 h-3 w-3 text-white drop-shadow" strokeWidth={2.5} />
                            </div>
                          ) : (
                            <div className="h-10 w-14 bg-neutral-100 border border-brand-line flex items-center justify-center flex-shrink-0">
                              <FileText className="h-4 w-4 text-neutral-500" />
                            </div>
                          )
                        ) : (
                          <img src={resolveMedia(n.image_url)} alt="" className="h-10 w-14 object-cover" />
                        )
                      )}
                      <div>
                        <p className="font-medium">{n.title}</p>
                        <p className="text-xs text-neutral-400">/news/{n.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-neutral-600">{formatDate(n.date)}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs ${n.status === "published" ? "bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-green-strong))]" : "bg-neutral-100 text-neutral-500"}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${n.status === "published" ? "bg-[hsl(var(--brand-green-strong))]" : "bg-neutral-400"}`} />
                      {n.status === "published" ? "Veröffentlicht" : "Entwurf"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Link to={`/admin/news/${n.id}`} className="text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]" data-testid={`edit-news-${n.id}`}><Pencil className="h-4 w-4" /></Link>
                      <button onClick={() => del(n.id)} data-testid={`delete-news-${n.id}`} className="text-neutral-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
